import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import DailyBreakdownTable from '../../departmentIncomeTracker/components/DailyBreakdownTable';
import IncomeDetailDialog from '../../departmentIncomeTracker/components/IncomeDetailDialog';
import { departmentIncomeTrackerTableHeader } from '../../departmentIncomeTracker/utils/departmentIncomeTrackerTableHeader';
import { formatCurrency, MONTH_TOTAL_ROW_ID, sortDepartments } from '../../departmentIncomeTracker/utils/deptDisplay';
import { useElementSize } from '../../departmentIncomeTracker/utils/layoutHooks';
import { compactInr, DEPT_ABBREVIATIONS } from '../utils/trackerConfig';
import { textFits, useFontsReady } from '../utils/textFit';

// Where the payments behind a clicked cell come from: the Project Tracker's own endpoint, which only ever returns the departments the
// viewer may see (the Accounts Manager page's endpoint is not narrowed that way).
const DETAIL_ENDPOINT = '/cro-project-tracker/department-income/detail';

/**
 * The Accounts Manager's daily table for the project departments: a row per day, a column per department, a Total column and the
 * month-total row pinned at the bottom. It scrolls inside `maxHeight` (the page gives it the same height as the CRO grid beside it, and
 * starts it on the same line); `expanded` (the Expand button) lifts that limit so every day shows and the page scrolls instead - its header
 * and month-total row then stay in view as the page moves. Click a cell for the payments behind it. The numbers are the Accounts Manager's own, narrowed on the
 * server to what the viewer may see.
 */
export default function ProjectDepartmentTable({ responseData, loading, error, month, skillxOnly, maxHeight, expanded = false }) {
  const meta = responseData?.meta;
  const todayDate = dayjs(month).isSame(dayjs(), 'month') ? dayjs().format('YYYY-MM-DD') : null;

  const departments = useMemo(() => sortDepartments(meta?.departments || []), [meta]);
  const rows = useMemo(() => (responseData?.data || []).map((row) => ({ ...row, ...(row.byDept || {}) })), [responseData?.data]);
  const maxByDept = useMemo(() => {
    const result = {};
    departments.forEach((dept) => {
      result[dept.deptId] = Math.max(0, ...rows.map((r) => r.byDept?.[dept.deptId] || 0));
    });
    return result;
  }, [departments, rows]);
  const maxTotal = useMemo(() => Math.max(0, ...rows.map((r) => r.total || 0)), [rows]);

  const monthTotalsRow = useMemo(() => {
    if (!rows.length) return undefined;
    const totals = { date: MONTH_TOTAL_ROW_ID, dayLabel: 'Total' };
    departments.forEach((dept) => {
      totals[String(dept.deptId)] = rows.reduce((sum, r) => sum + (Number(r[String(dept.deptId)]) || 0), 0);
    });
    totals.total = rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0);
    return totals;
  }, [departments, rows]);

  const [detailCell, setDetailCell] = useState(null);
  const handleCellClick = useCallback((date, deptId, deptName) => setDetailCell({ date, deptId, deptName }), []);
  // On a small screen the table works out how wide a column is. Amounts are whole rupees, and in lakh / crore (₹ 53.1L) only when the full
  // amount (the month total is the longest) would not fit; very narrow columns also shorten the department names. The tooltip keeps the full amount.
  const [boxRef, , boxW] = useElementSize();
  useFontsReady();
  const columnPx = boxW === null ? 120 : boxW / (departments.length + 2);
  const dense = columnPx < 74;
  // the month total is the longest amount in the table, so it decides (measured with the page's real font)
  // Decided COLUMN by column: a column writes its amounts in full whenever the biggest of them (the month total, pinned at the bottom) fits
  // the column; only a column that cannot is written in lakh / crore. The "₹ " is kept while every column still fits with it, else dropped.
  const fontPx = dense ? 11.2 : 12.2;
  const biggestOf = useCallback((key) => (monthTotalsRow ? Math.round(Number(monthTotalsRow[key]) || 0) : 0), [monthTotalsRow]);
  const columnKeys = useMemo(() => [...departments.map((d) => String(d.deptId)), 'total'], [departments]);
  const rupee = columnKeys.every((k) => textFits(formatCurrency(biggestOf(k)), columnPx, fontPx, true, 12));
  const shortCols = useMemo(() => new Set(columnKeys.filter((k) => !textFits(rupee ? formatCurrency(biggestOf(k)) : biggestOf(k).toLocaleString('en-IN'), columnPx, fontPx, true, 12))), [columnKeys, biggestOf, rupee, columnPx, fontPx]);
  const format = useCallback(
    (v, column) => {
      const whole = Math.round(Number(v) || 0);
      if (shortCols.has(column)) return `${rupee ? '₹ ' : ''}${compactInr(whole)}`;
      return rupee ? formatCurrency(whole) : whole.toLocaleString('en-IN');
    },
    [rupee, shortCols],
  );
  // a name is shortened only when the full one would not fit its column (it is never broken in the middle of a word)
  const headerName = useCallback((dept, shortName) => (textFits(shortName, columnPx, 12, true, 14) ? shortName : DEPT_ABBREVIATIONS[dept.deptId] || shortName), [columnPx]);
  const headers = useMemo(
    () => departmentIncomeTrackerTableHeader(departments, maxByDept, todayDate, maxTotal, handleCellClick, { format, headerName, todaySuffix: columnPx >= 100 }),
    [departments, maxByDept, todayDate, maxTotal, handleCellClick, format, headerName, columnPx],
  );

  if (error) return null; // the info panel above already shows the error and its Retry

  return (
    <div ref={boxRef}>
      <DailyBreakdownTable headers={headers} rows={rows} footerRow={monthTotalsRow} loading={loading && !meta} maxHeight={expanded ? undefined : maxHeight} fit dense={dense} />
      <IncomeDetailDialog
        open={!!detailCell}
        onClose={() => setDetailCell(null)}
        date={detailCell?.date}
        deptId={detailCell?.deptId ?? null}
        deptName={detailCell?.deptName}
        month={month}
        skillxOnly={skillxOnly}
        endpoint={DETAIL_ENDPOINT}
        fitWidth
      />
    </div>
  );
}
